/**
 * Recharge-attempt vocabulary for the admin panel: statuses, lifecycle stages,
 * reason codes, and the shape of `GET /admin/payments/orders/:id`.
 *
 * The API owns the classification — these are display labels only.
 */

export type OrderStatus =
  | 'created'
  | 'attempted'
  | 'captured'
  | 'failed'
  | 'cancelled'
  | 'abandoned'
  | 'refunded'
  | 'paid_credit_pending';

export type PaymentStage =
  | 'attempt_created'
  | 'payment_selection_opened'
  | 'payment_selection_exited'
  | 'provider_selected'
  | 'checkout_opened'
  | 'provider_processing'
  | 'provider_success'
  | 'provider_failure'
  | 'webhook_received'
  | 'verification'
  | 'verified'
  | 'verification_failed'
  | 'coin_credit'
  | 'coin_credit_failed'
  | 'completed'
  | 'cancelled'
  | 'expired'
  | 'refunded'
  | 'unknown';

export type FailureCode =
  | 'user_cancelled'
  | 'payment_selection_exited'
  | 'payment_declined'
  | 'insufficient_funds'
  | 'invalid_payment_method'
  | 'payment_timeout'
  | 'network_error'
  | 'provider_error'
  | 'provider_cancelled'
  | 'checkout_error'
  | 'invalid_order'
  | 'invalid_transaction'
  | 'verification_failed'
  | 'signature_verification_failed'
  | 'webhook_failed'
  | 'duplicate_transaction'
  | 'not_your_purchase'
  | 'package_not_found'
  | 'amount_mismatch'
  | 'currency_mismatch'
  | 'coin_credit_failed'
  | 'database_error'
  | 'attempt_expired'
  | 'unknown_error';

/**
 * Only `captured` means coins were credited. Everything else is an attempt that
 * moved no money — except `paid_credit_pending`, where the USER PAID and the
 * credit is still owed (red, because it needs attention).
 */
export const STATUS_COLOR: Record<OrderStatus, string> = {
  created: 'default',
  attempted: 'processing',
  captured: 'success',
  failed: 'error',
  cancelled: 'volcano',
  abandoned: 'default',
  refunded: 'warning',
  paid_credit_pending: 'magenta',
};

/** What each status means, in the words an operator would use. */
export const STATUS_HELP: Record<OrderStatus, string> = {
  created: 'Started, but the payment sheet may never have opened. No coins.',
  attempted: 'Reached the provider’s payment sheet, or the result is not known yet. PENDING. No coins.',
  captured: 'Verified and CREDITED — the only status that moved coins.',
  failed: 'The provider rejected the payment. No coins.',
  cancelled: 'The user backed out. No coins.',
  abandoned: 'Never resolved either way; swept automatically. A late success still credits. No coins.',
  refunded: 'Was credited, then reversed.',
  paid_credit_pending:
    'The user PAID, but the coin credit has not landed yet. Retried automatically — never a payment failure.',
};

export const STATUS_LABEL: Record<OrderStatus, string> = {
  created: 'Initiated',
  attempted: 'Pending',
  captured: 'Success',
  failed: 'Failed',
  cancelled: 'Cancelled',
  abandoned: 'Expired',
  refunded: 'Refunded',
  paid_credit_pending: 'Paid — credit pending',
};

export const ALL_STATUSES: OrderStatus[] = [
  'created',
  'attempted',
  'captured',
  'paid_credit_pending',
  'failed',
  'cancelled',
  'abandoned',
  'refunded',
];

export const STAGE_LABEL: Record<PaymentStage, string> = {
  attempt_created: 'Payment attempt created',
  payment_selection_opened: 'Payment selection opened',
  payment_selection_exited: 'Payment selection exited',
  provider_selected: 'Payment provider selected',
  checkout_opened: 'Checkout opened',
  provider_processing: 'Payment processing',
  provider_success: 'Provider reported success',
  provider_failure: 'Provider failure',
  webhook_received: 'Webhook received',
  verification: 'Payment verification',
  verified: 'Payment verified',
  verification_failed: 'Payment verification failed',
  coin_credit: 'Coins credited',
  coin_credit_failed: 'Coin credit failed',
  completed: 'Payment completed',
  cancelled: 'Payment cancelled',
  expired: 'Payment expired',
  refunded: 'Payment refunded',
  unknown: 'Unknown',
};

/** Stages that mean something went wrong — highlighted red on the timeline. */
const BAD_STAGES = new Set<PaymentStage>([
  'provider_failure',
  'verification_failed',
  'coin_credit_failed',
  'expired',
]);

export function stageColor(stage: PaymentStage | null): string {
  if (!stage) return 'gray';
  if (BAD_STAGES.has(stage)) return 'red';
  if (stage === 'completed' || stage === 'coin_credit' || stage === 'verified') return 'green';
  if (stage === 'cancelled' || stage === 'refunded' || stage === 'payment_selection_exited') return 'orange';
  return 'blue';
}

export const FAILURE_LABEL: Record<FailureCode, string> = {
  user_cancelled: 'User cancelled',
  payment_selection_exited: 'Left payment selection (no payment attempted)',
  payment_declined: 'Payment declined',
  insufficient_funds: 'Insufficient funds',
  invalid_payment_method: 'Invalid payment method',
  payment_timeout: 'Payment timed out',
  network_error: 'Network error (result unknown)',
  provider_error: 'Provider error',
  provider_cancelled: 'Cancelled by provider',
  checkout_error: 'Checkout error',
  invalid_order: 'Invalid order',
  invalid_transaction: 'Invalid transaction',
  verification_failed: 'Verification failed',
  signature_verification_failed: 'Signature verification failed',
  webhook_failed: 'Webhook processing failed',
  duplicate_transaction: 'Duplicate transaction',
  not_your_purchase: 'Purchase belongs to another account',
  package_not_found: 'Package not found',
  amount_mismatch: 'Amount mismatch',
  currency_mismatch: 'Currency mismatch',
  coin_credit_failed: 'Coin credit failed',
  database_error: 'Database error',
  attempt_expired: 'Attempt expired',
  unknown_error: 'Unknown error',
};

/** Reason code as shown to an operator: the stable code in capitals (what
 *  support tickets and logs quote) — the readable label goes beside it. */
export function reasonCodeText(code: FailureCode | null | undefined): string {
  return code ? code.toUpperCase() : '—';
}

export interface TimelineEntry {
  id: string;
  at: string;
  kind: 'lifecycle' | 'provider_event';
  eventType: string;
  stage: PaymentStage | null;
  stageLabel: string | null;
  status: OrderStatus | null;
  failureCode: FailureCode | null;
  reason: string | null;
  providerErrorCode: string | null;
  source: 'client' | 'webhook' | 'server' | 'worker' | null;
  processingError: string | null;
  metadata: Record<string, unknown> | null;
}

export interface PaymentDetail {
  id: string;
  /** displayName = the user's own Display Name — absent on older APIs. */
  user: { id: string; handle: string; displayName?: string | null };
  package: { id: string; name: string } | null;
  provider: 'razorpay' | 'google_play';
  amountPaise: string;
  currency: string;
  paidCoins: number | null;
  bonusCoins: number;
  status: OrderStatus;
  stage: PaymentStage;
  stageLabel: string;
  failureCode: FailureCode | null;
  failureReason: string | null;
  providerOrderId: string | null;
  providerPaymentId: string | null;
  providerTransactionId: string | null;
  providerError: {
    code: string | null;
    reason: string | null;
    message: string | null;
    source: string | null;
    step: string | null;
  };
  ucb: boolean;
  googleReportedAt: string | null;
  createdAt: string;
  updatedAt: string;
  paidAt: string | null;
  completedAt: string | null;
  closedAt: string | null;
  investigation: {
    providerOutcome: 'accepted' | 'rejected' | 'cancelled' | 'unknown';
    verification: 'succeeded' | 'failed' | 'not_reached';
    webhookReceived: boolean;
    webhookCount: number;
    webhookErrors: number;
    coinsCredited: boolean;
    paidCoinsCredited: string;
    bonusCoinsCredited: string;
    creditPending: boolean;
  };
  timeline: TimelineEntry[];
  ledger: Array<{
    id: string;
    at: string;
    account: string;
    direction: 'credit' | 'debit';
    reasonCode: string;
    amountCoins: string | null;
    amountPaise: string | null;
    idempotencyKey: string;
  }>;
}

export const PROVIDER_LABEL: Record<PaymentDetail['provider'], string> = {
  razorpay: 'Razorpay',
  google_play: 'Google Play',
};

/**
 * One-line verdict for the top of the drawer — the first thing an operator
 * needs, before any detail: did the user pay, and did they get their coins?
 */
export function paymentVerdict(p: Pick<PaymentDetail, 'status' | 'stage' | 'failureCode' | 'investigation'>): {
  type: 'success' | 'error' | 'warning' | 'info';
  text: string;
} {
  if (p.status === 'captured') return { type: 'success', text: 'Paid and credited.' };
  if (p.status === 'refunded') return { type: 'warning', text: 'Paid, credited, then refunded.' };
  if (p.status === 'paid_credit_pending' || p.investigation.creditPending) {
    return {
      type: 'error',
      text: 'The user PAID but the coins are not credited yet. Retried automatically every 10 minutes.',
    };
  }
  if (p.failureCode === 'payment_selection_exited') {
    return {
      type: 'info',
      text: 'The user left the payment selection without choosing a payment method. No payment was attempted — not a payment failure.',
    };
  }
  const where = STAGE_LABEL[p.stage];
  const why = p.failureCode ? ` — ${FAILURE_LABEL[p.failureCode]}` : '';
  if (p.status === 'cancelled') return { type: 'warning', text: `Cancelled at: ${where}${why}. No money moved.` };
  if (p.status === 'failed') return { type: 'error', text: `Failed at: ${where}${why}. No coins credited.` };
  if (p.status === 'abandoned') return { type: 'warning', text: `Expired unresolved at: ${where}${why}. No coins credited.` };
  return { type: 'info', text: `In progress — currently at: ${where}${why}. No coins credited yet.` };
}
